import React, { useState } from 'react';
import { Button, Textfield } from '@/components/atomics/';
import Pagebase from '@/components/organism/Pagebase';
import styles from './ListAdmin.module.scss';
import Icon from '@/components/atomics/Icons';
import Datatable from '@/components/atomics/Datatable';
import Chips from '@/components/atomics/Chips';
import Modal from '@/components/atomics/Modal';
import ActionsConfirm from '@/components/molecules/Actions/Confirm';
import ActionsMessage from '@/components/molecules/Actions/Message';
import ModalAddAdmin from '@/components/molecules/Modal/AddAdmin';
import { useAdmin, useDeleteAdmin } from '@/hooks/useAdmin';
import { extractRoleString } from '@/utils/admin';

const className = 'list-admin';

type AdminData = {
  id: string;
  username: string;
  role: string;
  level: string;
};

function getRoleLabel(level: number | string): string {
  switch (String(level)) {
    case '0':
      return 'Superadmin';
    case '1':
      return 'Supervisor';
    case '2':
      return 'CS';
    case '3':
      return 'CX';
    default:
      return 'Unknown';
  }
}

const ListAdmin: React.FC = () => {
  const [contentModal, setContentModal] = useState<React.ReactNode>();
  const [keyword, setKeyword] = useState<string | undefined>(undefined);
  const [confirmedKeyword, confirmKeyword] = useState<string | undefined>(undefined);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const { data, refetch, isLoading } = useAdmin();
  const { mutate: deleteAdmin, isPending: isLoadingDelete } = useDeleteAdmin();

  const handleClickAddAdmin = () => {
    setContentModal(
      <ModalAddAdmin
        open={true}
        onClose={() => setContentModal(undefined)}
        onSubmit={() => {
          setContentModal(undefined);
          handleSubmit('add');
        }}
      />
    );
  };

  const handleDeleteAdmin = (username: string) => {
    setContentModal(
      <Modal close={false} open={true} onClose={() => setContentModal(undefined)}>
        <ActionsConfirm
          isLoading={isLoadingDelete}
          title="Hapus Admin"
          message="Data admin akan dihapus secara permanen dari sistem Anda. Yakin ingin menghapus admin ini?"
          onConfirm={() => {
            deleteAdmin(username, {
              onSuccess: () => {
                if (admins.length === 1 && page > 1) {
                  setPage(page - 1);
                }
                handleSubmit('delete');
              },
              onError: (error) => {
              },
            });
          }}
          onCancel={() => setContentModal(undefined)}
        />
      </Modal>
    );
  };

  const handleClickEditAdmin = (id: string, username: string, role: string) => {
    setContentModal(
      <ModalAddAdmin
        open={true}
        onClose={() => setContentModal(undefined)}
        onSubmit={() => {
          setContentModal(undefined);
          handleSubmit('edit');
        }}
        role={role}
        username={username}
      />
    );
  };

  const handleSubmit = (action:  string) => {
    let title = '';
    let message = '';

    switch (action) {
      case 'add':
        title = 'Admin Berhasil Ditambah';
        message = 'Data admin baru telah berhasil disimpan dan siap digunakan.';
        break;
      case 'edit':        
        title = 'Admin Berhasil Diubah';
        message = 'Data admin telah berhasil disimpan dan siap digunakan.';
        break;
      case 'delete':
        title = 'Admin Berhasil Dihapus';
        message = 'Data admin telah berhasil dihapus dari sistem.';      
        break;
    }

    setContentModal(
      <Modal close={false} open={true} onClose={() => setContentModal(undefined)}>
        <ActionsMessage
          title={title}
          message={message}
          onConfirm={() => {
            refetch();
            setContentModal(undefined);
          }}
        />
      </Modal>
    );
  };

  const renderEmptyData = () => (
    <>
      <p>
        <img src="/images/icon-nodata.png" alt="No data" />
      </p>
      <label>
        Belum ada data admin yang terdaftar. Silakan tambahkan admin <br />untuk mulai mengelola.
      </label>
      <p>
        <Button
          className={styles[className + '__button_primary']}
          onClick={handleClickAddAdmin}
          variant="primary"
        >
          <Icon name="CirclePlus" /> Tambah Admin
        </Button>
      </p>
    </>
  );

  const filteredAdmins = Array.isArray(data)
    ? data.filter(
        (item: AdminData) =>
          !confirmedKeyword ||
          item.username.toLowerCase().includes(confirmedKeyword.toLowerCase()) ||
          getRoleLabel(item.level).toLowerCase().includes(confirmedKeyword.toLowerCase())
      )
    : [];
  
  const admins = filteredAdmins
    .slice((page - 1) * pageSize, page * pageSize)
    .map((item: AdminData, idx: number) => ({
      no: (page - 1) * pageSize + idx + 1,
      username: item.username,
      role: getRoleLabel(item.level),
      action: (
        <div className={styles[className + '__chips']}>
          <Chips
            className={styles[className + '__chip']}
            label={
              <div>
                <Icon name="Edit" /> Edit
              </div>
            }
            onClick={() => handleClickEditAdmin(item.id, item.username, extractRoleString(item?.level) ?? '')}
            variant="info"
          />
          <Chips
            className={styles[className + '__chip']}
            label={
              <div>
                <Icon name="Trash" /> Hapus
              </div>
            }
            onClick={() => handleDeleteAdmin(item.username)}
            variant="error"
          />
        </div>
      ),
    }));

  const totalPage = filteredAdmins.length ? Math.ceil(filteredAdmins.length / pageSize) : 1;

  return (
    <Pagebase className={styles[className]} title="Pengaturan / List Admin">
      <div className={styles[className + '__card']}>
        <div className={styles[className + '__card__form']}>
          <Textfield
            iconLeft={<Icon name="Search" />}
            iconRight={confirmedKeyword && <Icon name="Close"
              onClick={() => {
                setKeyword('');
                confirmKeyword('');
              }}/>}
            placeholder="Cari Admin"
            className={styles[className + '__card__form__textfield']}
            onChange={(e) => setKeyword(e.target.value)}
            value={keyword}
          />
          <Button className={styles[className + '__card__form__button']} disabled={!keyword}
            onClick={() => {
              confirmKeyword(keyword);
            }}>
            Cari
          </Button>
          <Button
            className={styles[className + '__button_outline']}
            onClick={handleClickAddAdmin}
            variant="outline-primary"
          >
            <Icon name="CirclePlus" /> Tambah Admin
          </Button>
        </div>
        <Datatable
          columns={[
            { label: 'No', key: 'no' },
            { label: 'Username', key: 'username' },
            { label: 'Role', key: 'role' },
            { label: 'Action', key: 'action' },
          ]}
          data={admins}
          emptyData={renderEmptyData()}
          isLoading={isLoading}
          page={page}
          totalPage={totalPage}
          onChangePage={setPage}
          onChangeSize={setPageSize}
        />
      </div>
      {
        contentModal
      }
    </Pagebase>
  );
};

export default ListAdmin;